import type { ApprovalEnvelopeV1 } from '@/types/approval';
import type { CitationPackV1Client, PressReleaseArtifactV1, PublishReadinessV1 } from '@/types/pressRelease';

const BLOCKER_LABELS: Record<string, string> = {
  missing_checkpoint: 'The run checkpoint is missing.',
  unknown_checkpoint: 'The checkpoint does not match this artifact.',
  unresolved_policy_issues: 'Resolve or dismiss policy issues first.',
  insufficient_rag: 'The draft does not have enough approved source support.',
  weak_citations: 'One or more citations are too weak for publishing.',
  unsupported_claims: 'One or more factual claims are unsupported.',
  unapproved_citation_sources: 'One or more citations use unapproved sources.',
  low_citation_precision: 'Citation precision is below the required threshold.',
  missing_approved_review: 'The reviewer has not approved this artifact yet.',
  missing_approval_envelope: 'Approval evidence is missing for this publish action.',
};

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeStatus(value: unknown): string {
  return normalizeString(value).toLowerCase();
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    const normalized = normalizeString(value);
    if (normalized) return normalized;
  }
  return null;
}

export function resolveArtifactCheckpointId(input: {
  checkpointId?: unknown;
  durableCheckpointId?: unknown;
  runId?: unknown;
  requestId?: unknown;
  metadata?: unknown;
  review?: unknown;
  data?: unknown;
  pressRelease?: unknown;
  pressReleaseV1?: unknown;
  readiness?: unknown;
}): string | null {
  const metadata = asRecord(input.metadata);
  const review = asRecord(input.review);
  const data = asRecord(input.data);
  const pressRelease = asRecord(
    input.pressRelease
    ?? input.pressReleaseV1
    ?? metadata.pressReleaseV1
    ?? metadata.pressRelease
    ?? data.pressReleaseV1
    ?? data.pressRelease,
  );
  const readiness = asRecord(
    input.readiness
    ?? pressRelease.readiness
    ?? pressRelease.publishReadiness,
  );

  return firstString(
    input.checkpointId,
    data.checkpointId,
    metadata.checkpointId,
    review.checkpointId,
    input.durableCheckpointId,
    data.durableCheckpointId,
    metadata.durableCheckpointId,
    pressRelease.checkpointId,
    readiness.checkpointId,
    input.runId,
    data.runId,
    metadata.runId,
    review.runId,
    input.requestId,
    data.requestId,
    metadata.requestId,
    review.requestId,
  );
}

export function formatApprovalBlocker(blocker: unknown): string {
  const key = normalizeString(blocker);
  if (!key) return 'Publishing is blocked until the artifact is ready.';
  return BLOCKER_LABELS[key] || key.replace(/_/g, ' ');
}

export function formatApprovalBlockers(blockers: unknown): string[] {
  if (!Array.isArray(blockers)) return [];
  return blockers.map(formatApprovalBlocker).filter(Boolean);
}

export function hasApprovedPublishEnvelope(
  approvalEnvelopes?: ApprovalEnvelopeV1[] | null,
  checkpointId?: string | null,
): boolean {
  const targetCheckpoint = normalizeString(checkpointId);
  return (approvalEnvelopes || []).some((envelope) => {
    if (envelope.version !== 'ApprovalEnvelopeV1') return false;
    if (envelope.actionClass !== 'publish') return false;
    if (envelope.decision !== 'approved') return false;
    if (!targetCheckpoint) return true;
    return envelope.checkpointId === targetCheckpoint;
  });
}

function unresolvedPolicyIssues(policyIssues?: Array<Record<string, unknown>> | null): number {
  return (policyIssues || []).filter((issue) => {
    const status = normalizeStatus(issue.status ?? issue.state ?? issue.resolution);
    return status !== 'resolved' && status !== 'dismissed' && status !== 'accepted';
  }).length;
}

export function citationPackUnsupportedCount(citationPack?: CitationPackV1Client | null): number {
  if (!citationPack) return 0;
  const metricValue = Number(citationPack.metrics?.unsupportedClaims);
  if (Number.isFinite(metricValue) && metricValue > 0) return metricValue;
  return citationPack.unsupportedClaims.length;
}

export function approvedSourceCount(citationPack?: CitationPackV1Client | null): number {
  if (!citationPack) return 0;
  return citationPack.sourceCards.filter((source) => {
    const status = normalizeStatus(source.approvedSourceStatus ?? source.status);
    return status === 'approved' || status === 'legacy';
  }).length;
}

export function buildReadinessFromArtifact(input: {
  status?: string;
  reviewState?: string;
  policyIssues?: Array<Record<string, unknown>>;
  citationPackV1?: CitationPackV1Client | null;
  checkpointId?: string | null;
  runId?: string | null;
  requestId?: string | null;
  metadata?: Record<string, unknown> | null;
  review?: Record<string, unknown> | null;
  approvalEnvelopes?: ApprovalEnvelopeV1[];
  pressRelease?: PressReleaseArtifactV1 | null;
  action?: 'approve' | 'publish';
}): PublishReadinessV1 {
  const pressReadiness = input.pressRelease?.readiness;
  const checkpointId = resolveArtifactCheckpointId({
    checkpointId: input.checkpointId,
    runId: input.runId,
    requestId: input.requestId,
    metadata: input.metadata,
    review: input.review,
    pressRelease: input.pressRelease,
    readiness: pressReadiness,
  });
  const blockers = new Set<string>(pressReadiness?.blockers || []);
  const warnings = new Set<string>(pressReadiness?.warnings || []);
  const openPolicy = unresolvedPolicyIssues(input.policyIssues);
  if (openPolicy > 0) blockers.add('unresolved_policy_issues');
  if (checkpointId) {
    blockers.delete('missing_checkpoint');
  } else {
    blockers.add('missing_checkpoint');
  }
  if (citationPackUnsupportedCount(input.citationPackV1 || input.pressRelease?.citationPack) > 0) {
    blockers.add('unsupported_claims');
  }

  const precision = Number((input.citationPackV1 || input.pressRelease?.citationPack)?.metrics?.citationPrecision);
  if (Number.isFinite(precision) && precision > 0 && precision < 0.75) blockers.add('low_citation_precision');

  if (input.action === 'publish') {
    const approved = normalizeStatus(input.status) === 'approved'
      || normalizeStatus(input.status) === 'published'
      || normalizeStatus(input.reviewState) === 'approved';
    if (!approved) blockers.add('missing_approved_review');
    if (!hasApprovedPublishEnvelope(input.approvalEnvelopes, checkpointId)) blockers.add('missing_approval_envelope');
  }

  const blockerList = Array.from(blockers).filter(Boolean);
  const warningList = Array.from(warnings).filter(Boolean);
  const inferredStatus = blockerList.length
    ? 'blocked'
    : warningList.length
      ? 'needs_attention'
      : (pressReadiness?.status === 'ready' ? 'ready' : 'unknown');

  return {
    version: 'PublishReadinessV1',
    status: pressReadiness?.status && pressReadiness.status !== 'unknown'
      ? (blockerList.length ? 'blocked' : pressReadiness.status)
      : inferredStatus,
    summary: pressReadiness?.summary || null,
    score: pressReadiness?.score ?? null,
    blockers: blockerList,
    warnings: warningList,
    checkpointId,
    policyStatus: openPolicy > 0 ? 'blocked' : (pressReadiness?.policyStatus || 'unknown'),
    citationStatus: blockerList.some((blocker) => blocker.includes('citation') || blocker === 'unsupported_claims')
      ? 'blocked'
      : (pressReadiness?.citationStatus || 'unknown'),
    ragStatus: blockerList.includes('insufficient_rag') ? 'blocked' : (pressReadiness?.ragStatus || 'unknown'),
  };
}
